import {
  memberRoleIsPractitioner,
  type PractitionerMemberRef,
  type PractitionerRoleRef,
} from '@/lib/practitionerRole';

export type PractitionerOptionMember = {
  user_id: string;
  status?: string | null;
  role?: PractitionerMemberRef['role'];
  custom_role_id?: string | null;
  working_hours?: unknown;
  user?: { name?: string | null; email?: string | null } | null;
};

export type PractitionerOptionFact = {
  practitioner_id: string | null;
  practitioner_name: string | null;
};

export type PractitionerReportOption = {
  id: string;
  label: string;
  /** Raw membership hours. Null when the person is only known from report rows. */
  hours: unknown | null;
};

function memberLabel(member: PractitionerOptionMember): string {
  const name = member.user?.name?.trim();
  if (name) return name;
  return member.user?.email?.trim() || member.user_id;
}

/**
 * Behandler options for the practitioner report.
 * When `roles` is set and scope is `practitioners`, only members whose role has
 * the Behandler flag are listed. People already chosen (`pinnedIds`) stay, and
 * people who appear in the result but are no longer active members stay selectable.
 * Scope `employees` (or calling without roles) lists every active member.
 */
export function practitionerReportOptions(
  members: PractitionerOptionMember[],
  facts: PractitionerOptionFact[] = [],
  options: {
    includeInactive?: boolean;
    roles?: PractitionerRoleRef[];
    scope?: 'practitioners' | 'employees';
    pinnedIds?: readonly string[];
  } = {},
): PractitionerReportOption[] {
  const includeInactive = Boolean(options.includeInactive);
  const roles = options.roles;
  const scope = options.scope ?? (roles ? 'practitioners' : 'employees');
  const pinned = new Set(options.pinnedIds ?? []);
  const byId = new Map<string, PractitionerReportOption>();

  for (const member of members) {
    if (!member.user_id) continue;
    if (!includeInactive && member.status && member.status !== 'active') continue;
    if (roles && scope === 'practitioners') {
      const flagged =
        member.role != null &&
        memberRoleIsPractitioner(
          { role: member.role, custom_role_id: member.custom_role_id ?? null },
          roles,
        );
      if (!flagged && !pinned.has(member.user_id)) continue;
    }
    if (byId.has(member.user_id)) continue;
    byId.set(member.user_id, {
      id: member.user_id,
      label: memberLabel(member),
      hours: member.working_hours ?? {},
    });
  }

  const membersById = new Map(members.map((member) => [member.user_id, member]));
  for (const fact of facts) {
    if (!fact.practitioner_id || byId.has(fact.practitioner_id)) continue;
    const known = membersById.get(fact.practitioner_id);
    if (known && roles && scope === 'practitioners' && !pinned.has(fact.practitioner_id)) {
      const active = !known.status || known.status === 'active';
      const flagged =
        known.role != null &&
        memberRoleIsPractitioner(
          { role: known.role, custom_role_id: known.custom_role_id ?? null },
          roles,
        );
      if (active && !flagged) continue;
    }
    const label = fact.practitioner_name?.trim() || fact.practitioner_id;
    byId.set(fact.practitioner_id, {
      id: fact.practitioner_id,
      label,
      hours: null,
    });
  }

  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
  );
}
